/**
 * 페이지 전환 컨트롤 — 사용자 / 워크스페이스 / 실시간 연결 공통.
 * 0-indexed page, total 0 이면 안 그림.
 */

import { Button } from '@/components/ui/button';
import { formatNumber } from './format';
import { useI18n } from './i18n';

interface PagerProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

export function Pager({ page, pageSize, total, onChange }: PagerProps) {
  const { t } = useI18n();
  if (total <= pageSize) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const prev = () => onChange(Math.max(0, page - 1));
  const next = () => onChange(Math.min(totalPages - 1, page + 1));

  return (
    <div className="mt-4 flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <div>
        {t('pager.total', { from: formatNumber(from), to: formatNumber(to), total: formatNumber(total) })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onChange(0)} disabled={page === 0}>{t('pager.first')}</Button>
        <Button variant="outline" size="sm" onClick={prev} disabled={page === 0}>{t('pager.prev')}</Button>
        <span className="px-2 text-xs text-muted-foreground">{page + 1} / {totalPages}</span>
        <Button variant="outline" size="sm" onClick={next} disabled={page >= totalPages - 1}>{t('pager.next')}</Button>
        <Button variant="outline" size="sm" onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1}>{t('pager.last')}</Button>
      </div>
    </div>
  );
}
